import { useState } from 'react';
import { MapPin, Calendar, Clock, ArrowRight } from 'lucide-react';
import { EventModal } from '../common/EventModal';
import { FALLBACK_EVENTS } from '../../utils/constants';
import { generateEventSlug } from '../../utils/helpers';
import { trackTicketClick } from '../../utils/tracking';
import { SoldOutCta } from '../common/SoldOutCta';

export const EventsPage = ({ events: propEvents }) => {
  const events = propEvents || FALLBACK_EVENTS;
  const [selectedEvent, setSelectedEvent] = useState(null);

  return (
    <>
      <div className="min-h-screen bg-[#0A0A0A] pt-24">
        <div className="container-custom py-16">
          <h1 className="font-display text-4xl md:text-6xl mb-4 text-center text-white">
            LATIN EVENTS<br /><span className="gradient-text">AMSTERDAM & NETHERLANDS</span>
          </h1>
          <p className="text-lg text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Experience the best Reggaeton, Dembow, and Latin Party events in the Netherlands.
          </p>
          
          {events.length === 0 && (
            <div
              data-testid="events-empty-state"
              className="max-w-xl mx-auto text-center border border-white/10 rounded-2xl py-16 px-8 bg-white/[0.02]"
            >
              <div className="text-5xl mb-4">🐰</div>
              <h2 className="font-display text-2xl md:text-3xl text-white mb-3">
                New dates dropping soon
              </h2>
              <p className="text-gray-400 mb-6">
                The next Baila Dembow edition is being announced. Follow along so
                you're the first to know when tickets open.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <a
                  href="https://www.instagram.com/baila.dembow/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF0080] text-white font-semibold hover:opacity-90 transition"
                >
                  Follow @baila.dembow
                </a>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...events].sort((a, b) => a.date.localeCompare(b.date)).map((event, index) => (
              <div key={event.id} className="event-card" data-testid={`events-page-card-${index}`}>
                <a href={`/events/${generateEventSlug(event)}`} className="block">
                  <div className="relative h-56 overflow-hidden">
                    <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <span className={`status-badge ${event.sold_out ? 'bg-black/85 text-white border border-white/30' : event.status === 'almost_sold_out' ? 'status-almost-sold-out' : 'status-upcoming'}`}>
                        {event.sold_out ? 'Sold Out' : event.status === 'almost_sold_out' ? 'Almost Sold Out' : 'On Sale'}
                      </span>
                    </div>
                  </div>
                </a>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-[#FF0080] font-semibold mb-2">
                    <MapPin size={14} /> {event.city}
                  </div>
                  <h3 className="font-display text-xl mb-2 text-white">{event.title}</h3>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-400 mb-4">
                    <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    <span className="flex items-center gap-1"><Clock size={14} /> {event.time}</span>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">{event.description.substring(0, 120)}...</p>
                  <div className="flex items-center justify-between">
                    {event.price_from ? (
                      <span className="text-sm text-gray-300">From <span className="font-bold text-lg text-white">{event.price_from}</span></span>
                    ) : <span />}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setSelectedEvent(event)}
                        className="text-sm text-gray-400 hover:text-[#FF0080] transition-colors underline underline-offset-2"
                      >
                        Quick View
                      </button>
                      {!event.sold_out && (
                        <a 
                          href={event.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[#FF0080] font-semibold hover:gap-2 transition-all"
                          onClick={() => trackTicketClick(event.title, event.ticket_url)}
                        >
                          Get Tickets <ArrowRight size={16} />
                        </a>
                      )}
                    </div>
                  </div>
                  {event.sold_out && (
                    <div className="mt-5 pt-5 border-t border-white/10">
                      <SoldOutCta tone="dark" compact testId={`events-page-sold-out-${index}`} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Wording approved by Hari (Developer Brief, Oct 2026, section D) */}
          <p className="text-gray-400 text-center max-w-2xl mx-auto mt-12 leading-relaxed">
            Baila Dembow runs reggaeton, dembow, salsa and bachata parties across the Netherlands, with nights in Amsterdam, Rotterdam, Leiden, Groningen and Eindhoven. Most events run from 23:00 to 05:00, are 18+ and need a valid ID at the door. Tickets are sold in rounds, so the earliest tickets are the cheapest and the price goes up closer to the date. Always buy through the ticket links on this page so you know your ticket is valid.
          </p>

          <div className="text-center mt-12">
            <a href="/" className="inline-flex items-center gap-2 text-[#FF0080] font-semibold hover:underline">
              ← Back to Homepage
            </a>
          </div>
        </div>
      </div>
      
      <EventModal 
        event={selectedEvent} 
        isOpen={!!selectedEvent} 
        onClose={() => setSelectedEvent(null)} 
      />
    </>
  );
};
